/**
 * Открытые столы зала.
 *
 * Официант ушёл домой, заболел, обслуживает другую компанию — а гость
 * сидит и хочет счёт. Без этого экрана администратору оставалось
 * искать официанта по залу или закрывать стол чужими руками.
 *
 * Здесь видны ВСЕ открытые заказы с суммами и именами. Свои открываются
 * сразу, чужие — с подтверждением: администратор берёт ответственность
 * осознанно, а в журнале остаётся, кто именно вмешался.
 */
import { useEffect, useState } from 'react';
import { useKeyboard } from './useKeyboard';
import { formatMoney } from '@dastarhan/core';
import type { OpenOrdersList } from '@dastarhan/contracts';
import { BASE, api } from '../api';
import { ElevateDialog } from './ElevateDialog';

type Row = OpenOrdersList['rows'][number];

export function OpenTablesScreen({ token, meId, meIsManager, canDelete = false, onOpen, onBack }: {
  token: string;
  meId: string;
  /** менеджер входит в чужой стол без чужого PIN — он и есть старший */
  meIsManager: boolean;
  /** право удалять ошибочно открытые столы */
  canDelete?: boolean;
  onOpen: (row: Row, approver: string | null) => void;
  onBack: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const [ask, setAsk] = useState<Row | null>(null);
  const [denied, setDenied] = useState<Row | null>(null);
  const [dropping, setDropping] = useState<Row | null>(null);
  const [dropReason, setDropReason] = useState('');
  const kb = useKeyboard(dropReason, setDropReason, 'text');

  const load = async () => {
    setBusy(true); setErr('');
    try {
      const d = await api.openOrders(token);
      setRows(d.rows);
    } catch {
      setErr('Не удалось загрузить. Нужна связь с сервером');
    } finally { setBusy(false); }
  };

  useEffect(() => { void load(); }, [token]);

  // В открытом заказе снятых позиций не бывает: они не доезжают
  // до сервера — снятие требует подтверждения и происходит на кассе
  /** Сколько минут гость ждёт с момента печати счёта. */
  const waitMin = (r: Row) => (r.precheckAt
    ? Math.max(0, Math.round((Date.now() - new Date(r.precheckAt).getTime()) / 60_000))
    : 0);

  const sumOf = (r: Row) =>
    r.items.reduce((s, i) => s + Math.round(i.qty * i.unitPrice), 0) - r.discount;

  // Ждут денег — первыми: гость уже получил счёт и сидит с деньгами
  // в руках, это самое срочное в зале
  // Пустой открытый стол — почти всегда промах по схеме зала.
  // Он держит место и путает: официант видит «занят» там, где никого
  const empty = rows.filter((r) => r.items.length === 0);
  const waiting = rows.filter((r) => r.awaitingPay && r.items.length > 0);
  const mine = rows.filter((r) => !r.awaitingPay && r.items.length > 0 && r.openedById === meId);
  const others = rows.filter((r) => !r.awaitingPay && r.items.length > 0 && r.openedById !== meId);

  const openRow = (r: Row) => {
    // Чужой стол официанту закрыт: там чужие гости и чужая выручка.
    // Сказать об этом прямо честнее, чем спросить PIN, которого у него нет
    if (r.openedById !== meId && !meIsManager) {
      setDenied(r);
      return;
    }
    if (r.openedById === meId || meIsManager) {
      // Менеджер — старший в смене, чужой PIN ему не нужен.
      // Но имя всё равно уходит в журнал: вмешательство должно быть видно
      onOpen(r, meIsManager && r.openedById !== meId ? 'менеджер' : null);
      return;
    }
    setAsk(r);
  };

  return (
    <div className="screen open-tables">
      <header>
        <button className="btn ghost" onClick={onBack}>← Назад</button>
        <h1>Открытые столы</h1>
        <button className="btn ghost" onClick={() => void load()}>Обновить</button>
      </header>

      {busy && <p className="hint">Загружаем…</p>}
      {err && <p className="error">{err}</p>}

      {!busy && rows.length === 0 && (
        <p className="hint">Открытых столов нет — весь зал закрыт.</p>
      )}

      {canDelete && empty.length > 0 && (
        <>
          <h2 className="ot-group">Пустые столы · {empty.length}</h2>
          <div className="ot-list">
            {empty.map((r) => (
              <div key={r.orderId} className="ot-row empty">
                <b className="ot-table">{r.tableName}</b>
                <span className="ot-items">ничего не набрано</span>
                <span className="ot-who">{r.openedByName}</span>
                <span />
                <button className="btn small" onClick={() => setDropping(r)}>
                  Удалить
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {waiting.length > 0 && (
        <>
          <h2 className="ot-group waiting">Ждут оплаты · {waiting.length}</h2>
          <div className="ot-list">
            {waiting.map((r) => (
              <button key={r.orderId} className="ot-row pay" onClick={() => openRow(r)}>
                <b className="ot-table">{r.tableName}</b>
                <span className="ot-items">{r.items.length} поз.</span>
                <span className="ot-who">{r.openedByName}</span>
                {/* Сколько гость ждёт с деньгами в руке. Пять минут —
                    норма, двадцать — повод поторопиться: он уже
                    оглядывается в поисках официанта */}
                <span className={`ot-time ${waitMin(r) >= 15 ? 'ot-late' : ''}`}>
                  ждёт {waitMin(r)} мин
                </span>
                <span className="ot-sum">{formatMoney(sumOf(r))}</span>
                <span className="ot-go">Принять оплату →</span>
              </button>
            ))}
          </div>
        </>
      )}

      {mine.length > 0 && (
        <>
          <h2 className="ot-group">Мои столы</h2>
          <div className="ot-list">
            {mine.map((r) => (
              <button key={r.orderId} className="ot-row mine" onClick={() => openRow(r)}>
                <b className="ot-table">{r.tableName}</b>
                <span className="ot-items">{r.items.length} поз.</span>
                <span className="ot-time">
                  {new Date(r.openedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="ot-sum">{formatMoney(sumOf(r))}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {others.length > 0 && (
        <>
          <h2 className="ot-group">
            Столы других официантов
            {!meIsManager && <small> · вам закрыты</small>}
          </h2>
          <div className="ot-list">
            {others.map((r) => (
              <button key={r.orderId}
                className={`ot-row ${!meIsManager ? 'locked' : ''}`}
                onClick={() => openRow(r)}>
                <b className="ot-table">{r.tableName}</b>
                <span className="ot-items">{r.items.length} поз.</span>
                <span className="ot-who">{r.openedByName}</span>
                <span className="ot-time">
                  {new Date(r.openedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="ot-sum">{formatMoney(sumOf(r))}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {dropping && (
        <div className="dialog-backdrop center" onClick={() => setDropping(null)}>
          <div className="dialog" onClick={(ev) => ev.stopPropagation()}>
            <h2>Удалить стол {dropping.tableName}</h2>
            <p className="hint">
              Стол пуст: заказа не было. Удаление уберёт его совсем —
              в отчёте следа не останется, потому что и продажи не было.
            </p>
            <label>Почему удаляем
              <input {...kb.props} value={dropReason} placeholder="открыл не тот стол"
                onChange={(ev) => setDropReason(ev.target.value)} />
            </label>
            {kb.node}
            <div className="dialog-actions">
              <button className="btn" onClick={() => setDropping(null)}>Отмена</button>
              <button className="btn danger" disabled={dropReason.trim().length < 3}
                onClick={() => {
                  void fetch(`${BASE}/open-orders/drop`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ orderId: dropping.orderId, reason: dropReason.trim() }),
                  }).then(() => { setDropping(null); setDropReason(''); void load(); });
                }}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}
      {denied && (
        <div className="dialog-backdrop center" onClick={() => setDenied(null)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>Стол {denied.tableName} — не ваш</h2>
            <p className="hint">
              Его ведёт {denied.openedByName}. Позовите его или старшего:
              у них есть доступ к этому столу.
            </p>
            <div className="dialog-actions">
              <button className="btn accent" onClick={() => setDenied(null)}>Понятно</button>
            </div>
          </div>
        </div>
      )}
      {ask && (
        <ElevateDialog
          title={`Открыть стол ${ask.tableName} · официант ${ask.openedByName}`}
          onCancel={() => setAsk(null)}
          onConfirm={async (pin) => {
            try {
              // Право то же, что на правку чужого заказа: кто может
              // снимать позиции, тот может и войти в чужой стол
              const r = await api.elevate(token, { pin, action: 'order.item.remove' });
              onOpen(ask, r.approver);
              setAsk(null);
              return r.approver;
            } catch { return null; }
          }}
        />
      )}
    </div>
  );
}
